import { styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Autocomplete, Box, Button, Form, Icon, Stack, Text } from 'shell/components';
import { COUNTRIES, EN_PROFICIENCY_OPTIONS, PRONOUNS_OPTIONS, TIMEZONES } from 'utils/constants';

const InformationSelectors = ({ device, loading, form, handleNext, handleChange, handleComplete }) => {
    const { t } = useTranslation('auth');

    return (
        <LoginBox device={device}>
            <Stack alignItems={'center'} spacing={1} sx={{ marginBottom: 6 }}>
                <Text variant={'h6'} weight={'medium'}>
                    {t('sign-up.get-to-know', 'Help us get to know you.')}
                </Text>
            </Stack>
            <Form onSubmit={() => handleComplete({ skip: false })}>
                <InputStack direction={'column'}>
                    <Stack direction={'row'} spacing={4}>
                        <DetailAutocomplete
                            data-cy={'account-creation-select-pronouns'}
                            value={form.pronoun}
                            label={t('sign-up.pronouns-label', 'Pronouns')}
                            labelId={'pronoun-select'}
                            options={PRONOUNS_OPTIONS.map((item) => ({ ...item, value: t(item.value) }))}
                            onChange={(_, selectedItem) => handleChange('pronoun', selectedItem)}
                        />
                        <DetailAutocomplete
                            data-cy={'account-creation-select-english-proficiency'}
                            value={form.englishProficiency}
                            label={t('sign-up.english-proficiency-label', 'English Proficiency')}
                            labelId={'english-proficiency-select'}
                            options={EN_PROFICIENCY_OPTIONS.map((item) => ({ ...item, value: t(item.value) }))}
                            onChange={(_, selectedItem) => handleChange('englishProficiency', selectedItem)}
                        />
                    </Stack>
                    <Stack direction={'row'} spacing={4}>
                        <DetailAutocomplete
                            data-cy={'account-creation-select-country'}
                            value={form.country}
                            label={t('sign-up.country-label', 'Country')}
                            labelId={'country-select'}
                            options={COUNTRIES?.map((item) => {
                                return {
                                    key: item.code,
                                    value: item.name,
                                };
                            })}
                            onChange={(_, selectedItem) => handleChange('country', selectedItem)}
                        />
                        <DetailAutocomplete
                            data-cy={'account-creation-select-timezone'}
                            value={form.timezone}
                            label={t('sign-up.timezone-label', 'Timezone')}
                            labelId={'timezone-select'}
                            options={TIMEZONES?.map((item) => {
                                return {
                                    key: item.text,
                                    value: item.text,
                                };
                            })}
                            onChange={(_, selectedItem) => handleChange('timezone', selectedItem)}
                        />
                    </Stack>
                </InputStack>
                <ButtonStack direction={'row'} justifyContent={'space-between'}>
                    <Button
                        color={'neutral'}
                        onClick={handleNext}
                        startIcon={<Icon kind={'arrowLeft'} />}
                        variant={'tertiary'}
                    >
                        {t('common:controls.back', 'Back')}
                    </Button>
                    <Stack direction={'row'} spacing={4}>
                        <Button
                            loading={loading}
                            color={'neutral'}
                            onClick={() => handleComplete({ skip: true })}
                            variant={'tertiary'}
                        >
                            {t('common:controls.skip-and-complete', 'Skip & Complete')}
                        </Button>
                        <Button
                            data-cy={'account-creation-complete-button'}
                            loading={loading}
                            size={'medium'}
                            type={'submit'}
                        >
                            {t('common:controls.complete', 'Complete')}
                        </Button>
                    </Stack>
                </ButtonStack>
            </Form>
        </LoginBox>
    );
};

const LoginBox = styled(Box)(({ theme, device }) => {
    return {
        marginTop: theme.spacing(12),
        padding: device === 'mobile' ? 0 : theme.spacing(7),
        width: device === 'mobile' ? '100%' : 564,
        height: 'fit-content',
        background: device === 'mobile' ? 'transparent' : theme.palette.ensightful.shades[0],
        boxShadow: device === 'mobile' ? 'none' : theme.palette.ensightful.shadows[1],
        borderRadius: '12px',
    };
});

const InputStack = styled(Stack)(({ theme }) => {
    return {
        margin: theme.spacing(6, 0, 2, 0),
        gap: theme.spacing(4),
    };
});

const ButtonStack = styled(Stack)(({ theme }) => {
    return {
        margin: theme.spacing(6, 0, 3, 0),
    };
});

const DetailAutocomplete = styled(Autocomplete)(() => {
    return { width: '100%' };
});
export default InformationSelectors;
